/**
 * POS Payment React Query Hooks
 * Handles payment operations (cash, card, partial, complete)
 */

import { ApiError, extractErrorBody } from '@/api/client';
import type {
  AddPartialPaymentDto,
  CardPaymentResult,
  CashPaymentResult,
  ProcessCardPaymentDto,
  ProcessCashPaymentDto,
  SalePayment,
  SaleTransaction,
} from '@/types/pos.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders } from './use-pos-session';
import { posTransactionKeys } from './use-pos-transaction';

const API_BASE = '/api/v1/pos';

// Helper: parse NestJS error response
function parseErrorMessage(error: Record<string, unknown>, fallback: string): string {
  return (error.message as string) || (error.error as { message?: string })?.message || fallback;
}

// Helper: unwrap { data: T } or raw T response
function unwrap<T>(result: unknown): T {
  const obj = result as { data?: T };
  return obj.data ?? (result as T);
}

// ============================================
// API Functions
// ============================================

async function processCashPayment(
  transactionId: string,
  dto: ProcessCashPaymentDto
): Promise<CashPaymentResult> {
  const response = await fetch(`${API_BASE}/transactions/${transactionId}/payments/cash`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseErrorMessage(error, 'Failed to process cash payment'));
  }

  return unwrap<CashPaymentResult>(await response.json());
}

async function processCardPayment(
  transactionId: string,
  dto: ProcessCardPaymentDto
): Promise<CardPaymentResult> {
  const response = await fetch(`${API_BASE}/transactions/${transactionId}/payments/card`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseErrorMessage(error, 'Failed to process card payment'));
  }

  return unwrap<CardPaymentResult>(await response.json());
}

async function addPartialPayment(
  transactionId: string,
  dto: AddPartialPaymentDto
): Promise<{ payment: SalePayment; transaction: SaleTransaction }> {
  const response = await fetch(`${API_BASE}/transactions/${transactionId}/payments`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseErrorMessage(error, 'Failed to add partial payment'));
  }

  return unwrap<{ payment: SalePayment; transaction: SaleTransaction }>(await response.json());
}

async function completeTransaction(transactionId: string): Promise<SaleTransaction> {
  const response = await fetch(`${API_BASE}/transactions/${transactionId}/complete`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseErrorMessage(error, 'Failed to complete transaction'));
  }

  return unwrap<SaleTransaction>(await response.json());
}

async function finalizeTransaction(transactionId: string): Promise<SaleTransaction> {
  const response = await fetch(`${API_BASE}/transactions/${transactionId}/finalize`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseErrorMessage(error, 'Failed to finalize transaction'));
  }

  return unwrap<SaleTransaction>(await response.json());
}

async function fetchTransactionPayments(transactionId: string): Promise<SalePayment[]> {
  const response = await fetch(`${API_BASE}/transactions/${transactionId}/payments`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseErrorMessage(error, 'Failed to fetch payments'));
  }

  const result = await response.json();
  // May return { data: [...] } or [...]
  return (result as { data?: SalePayment[] }).data ?? (result as SalePayment[]);
}

// ============================================
// React Query Hooks
// ============================================

/**
 * Hook to process cash payment
 */
export function useProcessCashPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, dto }: { transactionId: string; dto: ProcessCashPaymentDto }) =>
      processCashPayment(transactionId, dto),
    onSuccess: result => {
      // Update transaction in cache
      if (result.transaction) {
        queryClient.setQueryData(
          posTransactionKeys.detail(result.transaction.id),
          result.transaction
        );
      }
    },
  });
}

/**
 * Hook to process card payment
 */
export function useProcessCardPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, dto }: { transactionId: string; dto: ProcessCardPaymentDto }) =>
      processCardPayment(transactionId, dto),
    onSuccess: result => {
      // Update transaction in cache
      if (result.transaction) {
        queryClient.setQueryData(
          posTransactionKeys.detail(result.transaction.id),
          result.transaction
        );
      }
    },
  });
}

/**
 * Hook to add partial payment (for mixed payments)
 */
export function useAddPartialPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, dto }: { transactionId: string; dto: AddPartialPaymentDto }) =>
      addPartialPayment(transactionId, dto),
    onSuccess: result => {
      // Update transaction in cache
      queryClient.setQueryData(
        posTransactionKeys.detail(result.transaction.id),
        result.transaction
      );
    },
  });
}

/**
 * Hook to complete a transaction (mark ready for payment)
 */
export function useCompleteTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: completeTransaction,
    onSuccess: transaction => {
      queryClient.setQueryData(posTransactionKeys.detail(transaction.id), transaction);
      queryClient.invalidateQueries({ queryKey: posTransactionKeys.all });
    },
  });
}

/**
 * Hook to finalize a transaction (deduct inventory after payment)
 */
export function useFinalizeTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: finalizeTransaction,
    onSuccess: transaction => {
      queryClient.setQueryData(posTransactionKeys.detail(transaction.id), transaction);
      queryClient.invalidateQueries({ queryKey: posTransactionKeys.all });
    },
  });
}

/**
 * Query key for transaction payments
 */
export const posPaymentKeys = {
  all: ['pos-payments'] as const,
  byTransaction: (transactionId: string) => [...posPaymentKeys.all, transactionId] as const,
};

/**
 * Hook to fetch payments for a transaction
 */
export function useTransactionPayments(transactionId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: posPaymentKeys.byTransaction(transactionId),
    queryFn: () => fetchTransactionPayments(transactionId),
    enabled: options?.enabled ?? !!transactionId,
  });
}

// ============================================
// Helper Types for Payment Flow
// ============================================

export interface PaymentFlowState {
  transactionId: string;
  total: number;
  paidAmount: number;
  remainingAmount: number;
  payments: SalePayment[];
  isComplete: boolean;
}

/**
 * Calculate remaining amount from transaction
 */
export function calculatePaymentState(transaction: SaleTransaction): PaymentFlowState {
  const paidAmount = transaction.paidAmount;
  const remainingAmount = Math.max(0, transaction.total - paidAmount);

  return {
    transactionId: transaction.id,
    total: transaction.total,
    paidAmount,
    remainingAmount,
    payments: transaction.payments,
    isComplete: transaction.paymentStatus === 'PAID',
  };
}

// ============================================
// LOYALTY REDEMPTION (SALES-FG-4-12)
// ============================================

export interface LoyaltyRedemptionDto {
  transactionId: string;
  partnerId: string;
  pointsToRedeem: number;
}

export interface LoyaltyRedemptionResult {
  data: {
    pointsRedeemed: number;
    amountDeducted: number;
    newBalance: number;
    transactionPaidAmount: number;
    isFullyPaid: boolean;
  };
}

export interface LoyaltyBalanceResult {
  data: {
    partnerId: string;
    balance: number;
    balanceHuf: number;
  };
}

async function redeemLoyaltyPoints(dto: LoyaltyRedemptionDto): Promise<LoyaltyRedemptionResult> {
  const response = await fetch(`${API_BASE}/redeem-loyalty`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
  });
  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    // #603 contract: a redeem-elutasítás strukturált { code, message }-t ad
    // (LOYALTY_INSUFFICIENT / LOYALTY_CONCURRENT). ApiError-ben propagáljuk a code-ot,
    // hogy a FE i18n-biztosan branch-eljen (nem message-substring). extractErrorBody
    // a nested ({ error: { code } }) és a flat ({ code }) NestJS-shape-et is kezeli.
    const { code, message } = extractErrorBody(errorData);
    throw new ApiError(
      message ?? parseErrorMessage(errorData, 'Failed to redeem loyalty points'),
      code,
      response.status
    );
  }
  return unwrap(await response.json());
}

async function fetchLoyaltyBalance(partnerId: string): Promise<LoyaltyBalanceResult> {
  const response = await fetch(`${API_BASE}/loyalty-balance/${partnerId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      parseErrorMessage(errorData as Record<string, unknown>, 'Failed to fetch loyalty balance')
    );
  }
  return unwrap(await response.json());
}

/**
 * Hook to redeem loyalty points in POS checkout (SALES-FG-4-12)
 */
export function useRedeemLoyalty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: LoyaltyRedemptionDto) => redeemLoyaltyPoints(dto),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: posPaymentKeys.byTransaction(variables.transactionId),
      });
      void queryClient.invalidateQueries({ queryKey: posTransactionKeys.all });
    },
  });
}

/**
 * Hook to get partner loyalty balance for POS display (SALES-FG-4-12)
 */
export function usePosLoyaltyBalance(partnerId: string | undefined) {
  return useQuery({
    queryKey: ['pos-loyalty-balance', partnerId],
    queryFn: () => fetchLoyaltyBalance(partnerId!),
    enabled: !!partnerId,
  });
}
